import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { ConsentProvider } from "@/contexts/ConsentContext";
import { Toaster } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { trackPageView } from "@/lib/analytics";

import Home from "@/pages/Home";
import Archive from "@/pages/Archive";
import EncounterDetail from "@/pages/EncounterDetail";
import EncounterMatches from "@/pages/EncounterMatches";
import Lodge from "@/pages/Lodge";
import Species from "@/pages/Species";
import SpeciesDetail from "@/pages/SpeciesDetail";
import WorldMap from "@/pages/WorldMap";
import Timeline from "@/pages/Timeline";
import Universe from "@/pages/Universe";
import MediaArchive from "@/pages/MediaArchive";
import MediaArchiveDetail from "@/pages/MediaArchiveDetail";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import MySubmissions from "@/pages/MySubmissions";
import MyMedia from "@/pages/MyMedia";
import Messages from "@/pages/Messages";
import CommunityFeed from "@/pages/CommunityFeed";
import Collections from "@/pages/Collections";
import CollectionDetail from "@/pages/CollectionDetail";
import Analytics from "@/pages/Analytics";
import AuthCallback from "@/pages/AuthCallback";
import Profile from "@/pages/Profile";
import UserProfile from "@/pages/UserProfile";
import Leaderboard from "@/pages/Leaderboard";
import ThumbnailCuration from "@/pages/ThumbnailCuration";
import Pricing from "@/pages/Pricing";
import BillingSuccess from "@/pages/BillingSuccess";
import Contact from "@/pages/Contact";
import About from "@/pages/About";
import Privacy from "@/pages/Privacy";
import CookiePolicy from "@/pages/CookiePolicy";

function RouteTracker() {
    const location = useLocation();
    useEffect(() => {
        const path = location.pathname + (location.search || "");
        // page title is set by document.title; we sanitise inside trackPageView.
        trackPageView(path, document.title);
    }, [location.pathname, location.search]);
    return null;
}

function AppRouter() {
    const location = useLocation();
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <>
            <RouteTracker />
            <Header />
            <main className="min-h-[60vh]">
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/encounters" element={<Archive />} />
                    <Route path="/encounters/:id" element={<EncounterDetail />} />
                    <Route path="/encounters/:id/matches" element={<EncounterMatches />} />
                    <Route path="/lodge" element={<Lodge />} />
                    <Route path="/species" element={<Species />} />
                    <Route path="/species/:slug" element={<SpeciesDetail />} />
                    <Route path="/map" element={<WorldMap />} />
                    <Route path="/timeline" element={<Timeline />} />
                    <Route path="/universe" element={<Universe />} />
                    <Route path="/media-archive" element={<MediaArchive />} />
                    <Route path="/media-archive/:slug" element={<MediaArchiveDetail />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/my-submissions" element={<MySubmissions />} />
                    <Route path="/me/media" element={<MyMedia />} />
                    <Route path="/me/analytics" element={<Analytics />} />
                    <Route path="/messages" element={<Messages />} />
                    <Route path="/messages/:peerId" element={<Messages />} />
                    <Route path="/community" element={<CommunityFeed />} />
                    <Route path="/feed" element={<CommunityFeed />} />
                    <Route path="/collections" element={<Collections />} />
                    <Route path="/collections/:cid" element={<CollectionDetail />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/u/:userId" element={<UserProfile />} />
                    <Route path="/leaderboard" element={<Leaderboard />} />
                    <Route path="/admin/thumbnail-curation" element={<ThumbnailCuration />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/billing/success" element={<BillingSuccess />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/cookie-policy" element={<CookiePolicy />} />
                </Routes>
            </main>
            <Footer />
            <CookieConsentBanner />
        </>
    );
}

export default function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <ConsentProvider>
                    <BrowserRouter>
                        <AppRouter />
                        <Toaster position="top-right" richColors />
                    </BrowserRouter>
                </ConsentProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}
